import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AccountLinkingModal from './auth/AccountLinkingModal';
import { useAccountLinking } from './auth/useAccountLinking';
import { useJWTAuth } from './auth/JWTAuthContext';
import { useAccountJWTSync } from './auth/useAccountJWTSync';
import { useToast } from './contexts/ToastContext';
import AuthGuard from './components/AuthGuard';

const Profile = () => {
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [activeWarrior, setActiveWarrior] = useState(null);
  const [heroesLoading, setHeroesLoading] = useState(false);
  const { user, setUser, loadWallets } = useAccountLinking();
  const { user: jwtUser, isAuthenticated: isJWTAuthenticated, login: jwtLogin } = useJWTAuth();
  const { info } = useToast();
  
  // Sync wallet authentication with JWT authentication (bidirectional)
  useAccountJWTSync(user, setUser);

  // Removed automatic modal opening - user can click "Manage Account" button instead

  

  const fetchActiveWarrior = useCallback(async () => {
    if (!user?.id) return;

    setHeroesLoading(true);
    try {
      // Get the most recently active hero (based on hero_battles with non-null final_position)
      const response = await fetch(`/api/heroes/user/${user.id}/most-recent-active`);
      const data = await response.json();

      if (data.success && data.data) {
        setActiveWarrior(data.data);
      } else {
        setActiveWarrior(null);
      }
    } catch (error) {
      console.error('Error fetching most recently active hero:', error);
      setActiveWarrior(null);
    } finally {
      setHeroesLoading(false);
    }
  }, [user]);

  // Fetch active Hero when user is available
  useEffect(() => {
    if (user && user.id) {
      fetchActiveWarrior();
    }
  }, [user, fetchActiveWarrior]);


  return (
    <AuthGuard>
      <div className="min-h-screen bg-cover bg-center bg-no-repeat" style={{
        backgroundImage: `url('/assets/menus/SiteBackground.jpg')`
      }}>
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black bg-opacity-40"></div>

        {/* Main Content */}
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">
            <span className="text-yellow-400 text-stroke-black drop-shadow-lg">
              Profile
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
            Manage your account, link wallets, and track your gaming progress.
          </p>
        </div>


        {/* Profile Content */}
        <div className="grid md:grid-cols-2 gap-8">
          
          {/* Hero Card */}
          {user && (
            <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-8 border border-white border-opacity-20">
              <div className="text-center mb-6">
                <div className="text-6xl mb-4">⚔️</div>
                <h2 className="text-2xl font-bold text-white mb-2">Spotlight Hero</h2>
                <p className="text-gray-300">
                  {activeWarrior ? 'Your most recent active hero' : 'Create your first hero'}
                </p>
              </div>

              {heroesLoading ? (
                <div className="text-center py-8">
                  <div className="text-gray-400">Loading hero...</div>
                </div>
              ) : activeWarrior ? (
                <div className="space-y-4">
                  <div className="bg-gray-800 bg-opacity-50 rounded-lg p-4">
                    <div className="text-white font-semibold text-lg">{activeWarrior.name}</div>
                    <div className="text-yellow-400 text-sm">Level {activeWarrior.level}</div>
                  </div>
                  
                  <div className="bg-gray-800 bg-opacity-50 rounded-lg p-4">
                    <div className="text-sm text-gray-400 mb-1">Experience</div>
                    <div className="text-white font-semibold">{activeWarrior.unspent_xp.toLocaleString()} XP available</div>
                    <div className="text-gray-400 text-sm">{activeWarrior.total_xp.toLocaleString()} total XP</div>
                  </div>

                  <div className="flex gap-2">
                    <Link
                      to={`/hero/${activeWarrior.id}`}
                      className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-2 px-4 rounded-lg text-sm transition-all duration-200 transform hover:scale-105 text-center"
                    >
                      Manage Hero
                    </Link>
                    <Link
                      to="/heroes"
                      className="flex-1 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white font-bold py-2 px-4 rounded-lg text-sm transition-all duration-200 transform hover:scale-105 text-center"
                    >
                      All Heroes
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-gray-800 bg-opacity-50 rounded-lg p-4 text-center">
                    <div className="text-gray-400 mb-2">No Hero yet</div>
                    <div className="text-sm text-gray-500">
                      Create your first hero to start earning XP
                    </div>
                  </div>
                  <Link
                    to="/heroes"
                    className="w-full bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl text-center block"
                  >
                    ⚔️ Create Hero
                  </Link>
                </div>
              )}
            </div>
          )}
          
          {/* Account Status Card */}
          <div className="bg-black bg-opacity-60 backdrop-blur-sm rounded-xl p-8 border border-white border-opacity-20">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">
                {user ? '👤' : '🔗'}
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">
                {user ? 'Account Connected' : 'No Wallet Linked'}
              </h2>
              <p className="text-gray-300">
                {user 
                  ? `Welcome back, ${user.username}!` 
                  : 'Link your wallets to create a unified gaming identity.'
                }
              </p>
            </div>

            {user ? (
              <div className="space-y-4">
                <div className="bg-gray-800 bg-opacity-50 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Username</div>
                  <div className="text-white font-semibold text-lg">{user.username}</div>
                </div>
                <div className="bg-gray-800 bg-opacity-50 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">User ID</div>
                  <div className="text-white font-mono text-sm">{user.id}</div>
                </div>
                <button
                  onClick={() => setShowAccountModal(true)}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  ⚙️ Manage Account
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-gray-800 bg-opacity-50 rounded-lg p-4 text-center">
                  <div className="text-gray-400 mb-2">No account linked yet</div>
                  <div className="text-sm text-gray-500">
                    Link your wallets to get started
                  </div>
                </div>
                <button
                  onClick={() => setShowAccountModal(true)}
                  className="w-full bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                >
                  🔗 Link Account
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-12">
          <div className="grid md:grid-cols-2 gap-6">

            <div className="bg-black bg-opacity-50 backdrop-blur-sm rounded-lg p-6 border border-white border-opacity-20 text-center">
              <div className="text-3xl mb-4">🛒</div>
              <h3 className="text-lg font-semibold text-white mb-2">Shop</h3>
              <p className="text-gray-300 text-sm mb-4">Browse items, themes, and exclusive content</p>
              <button
                onClick={() => info('Shop feature coming soon!')}
                className="inline-block bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-bold py-2 px-4 rounded-lg text-sm transition-all duration-200 transform hover:scale-105"
              >
                Visit Shop
              </button>
            </div>
            

            <div className="bg-black bg-opacity-50 backdrop-blur-sm rounded-lg p-6 border border-white border-opacity-20 text-center">
              <div className="text-3xl mb-4">🎨</div>
              <h3 className="text-lg font-semibold text-white mb-2">Create Themes</h3>
              <p className="text-gray-300 text-sm mb-4">Design custom game themes and share them</p>
              <button
                onClick={() => info('Theme creation feature coming soon!')}
                className="inline-block bg-gradient-to-r from-pink-600 to-red-600 hover:from-pink-700 hover:to-red-700 text-white font-bold py-2 px-4 rounded-lg text-sm transition-all duration-200 transform hover:scale-105"
              >
                Request Access
              </button>
            </div>

            {/*<div className="bg-black bg-opacity-50 backdrop-blur-sm rounded-lg p-6 border border-white border-opacity-20 text-center">
              <div className="text-3xl mb-4">🎮</div>
              <h3 className="text-lg font-semibold text-white mb-2">Play Game</h3>
              <p className="text-gray-300 text-sm mb-4">Jump into the action and start battling</p>
              <a
                href="/game"
                className="inline-block bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-2 px-4 rounded-lg text-sm transition-all duration-200 transform hover:scale-105"
              >
                Enter Arena
              </a>
            </div>*/}

            
          </div>
        </div>

        {/* Security Notice */}
        <div className="mt-12 bg-blue-900 bg-opacity-80 border border-blue-700 rounded-xl p-6">
          <div className="flex items-start space-x-3">
            <div className="text-2xl">🔒</div>
            <div>
              <h3 className="text-lg font-semibold text-blue-100 mb-2">Security & Privacy</h3>
              <p className="text-blue-200 text-sm leading-relaxed">
                Your account linking is secured through message signing verification. We never store or have access to your private keys. 
                Each wallet connection requires you to sign a unique message to prove ownership. Your data is encrypted and stored securely.
              </p>
            </div>
          </div>
        </div>

        {/* Debug Authentication Status */}
        <div className="mt-8 bg-gray-800 bg-opacity-50 rounded-lg p-4 border border-gray-600">
          <h3 className="text-lg font-bold text-white mb-4">🔧 Debug: Authentication Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-gray-400 mb-2">Wallet Auth:</div>
              <div className="text-white">
                {user ? `✅ Logged in as ${user.username} (ID: ${user.id})` : '❌ Not logged in'}
              </div>
              {user && (
                <div className="text-gray-300 text-xs mt-1">
                  Wallet: {user.wallet_address}
                </div>
              )}
            </div>
            <div>
              <div className="text-gray-400 mb-2">JWT Auth:</div>
              <div className="text-white">
                {isJWTAuthenticated() ? `✅ JWT authenticated as ${jwtUser?.username} (ID: ${jwtUser?.id})` : '❌ Not JWT authenticated'}
              </div>
              {!isJWTAuthenticated() && user && (
                <button
                  onClick={async () => {
                    console.log('🔧 Manual JWT login triggered');
                    const result = await jwtLogin(user.wallet_address);
                    console.log('🔧 Manual JWT login result:', result);
                  }}
                  className="mt-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs"
                >
                  🔧 Manual JWT Login
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

        {/* Account Linking Modal */}
        <AccountLinkingModal
          isOpen={showAccountModal}
          onClose={() => setShowAccountModal(false)}
          currentUser={user}
          onUserUpdate={(newUser) => {
            console.log('🎯 Profile onUserUpdate called with:', newUser);
            try {
              if (newUser) {
                console.log('👤 Setting user in Profile:', newUser);
                setUser(newUser);
                console.log('🔄 Loading wallets in Profile');
                loadWallets();
              }
            } catch (error) {
              console.error('❌ Error in Profile onUserUpdate:', error);
            }
          }}
        />
      </div>
    </AuthGuard>
  );
};

export default Profile;
